import type { CSSProperties, ReactNode } from 'react';
import { defineMessages } from '../../i18n/define';
import { tr } from '../../i18n/runtime';
import './skeleton.css';

const m = defineMessages({
  loading: {
    id: 'common.ui.skeleton.loading',
    defaultMessage: 'Загружаю {label}…',
    description: 'Фраза для чтения с экрана, пока блок грузится; {label} — что грузится, в винительном падеже («карту», «таблицу»)',
  },
  kpi: { id: 'common.ui.skeleton.kpi', defaultMessage: 'показатель', description: 'Что грузится (в винительном падеже) — подпись скелетона KPI-карточки для чтения с экрана' },
  card: { id: 'common.ui.skeleton.card', defaultMessage: 'блок', description: 'Что грузится (в винительном падеже) — подпись скелетона карточки для чтения с экрана' },
  chart: { id: 'common.ui.skeleton.chart', defaultMessage: 'график', description: 'Что грузится (в винительном падеже) — подпись скелетона графика для чтения с экрана' },
  table: { id: 'common.ui.skeleton.table', defaultMessage: 'таблицу', description: 'Что грузится (в винительном падеже) — подпись скелетона таблицы для чтения с экрана' },
  map: { id: 'common.ui.skeleton.map', defaultMessage: 'карту', description: 'Что грузится (в винительном падеже) — подпись скелетона карты для чтения с экрана' },
  list: { id: 'common.ui.skeleton.list', defaultMessage: 'список', description: 'Что грузится (в винительном падеже) — подпись скелетона списка для чтения с экрана' },
  page: { id: 'common.ui.skeleton.page', defaultMessage: 'раздел', description: 'Что грузится (в винительном падеже) — подпись скелетона страницы для чтения с экрана' },
});

/**
 * Скелетоны загрузки (agroai-6al.49, журнал №520–522).
 *
 * Правило страницы: пока данные идут, на экране стоит та же раскладка, что
 * будет после загрузки, — шапка, сетка, карточки в своих размерах. Скелетон
 * повторяет форму блока (KPI-карточка, график, таблица, карта, список), поэтому
 * данные подставляются без сдвига раскладки. «Нет данных» до конца загрузки не
 * говорится никогда: скелетон — это «ещё не знаем», а не «пусто».
 *
 * Доступность: визуальные плашки скрыты от чтения (`aria-hidden`), блок целиком
 * — `role="status"` + `aria-busy` с одной фразой «Загружаю …». Анимация —
 * мягкий проблеск без движения раскладки; при `prefers-reduced-motion` её нет.
 */

type Size = number | string;

const px = (v: Size | undefined): string | undefined => (typeof v === 'number' ? `${v}px` : v);

export interface SkeletonProps {
  /** `line` — строка текста, `block` — прямоугольник, `circle` — круг. */
  variant?: 'line' | 'block' | 'circle';
  width?: Size;
  height?: Size;
  className?: string;
  style?: CSSProperties;
}

/** Одна плашка. Сама ничего не объявляет: объявляет обёртка `SkeletonRegion`. */
export function Skeleton({ variant = 'line', width, height, className, style }: SkeletonProps) {
  const size = variant === 'circle' ? (height ?? width ?? 24) : undefined;
  return (
    <span
      aria-hidden="true"
      className={`skel skel--${variant} ${className ?? ''}`}
      style={{
        width: px(size ?? width),
        height: px(size ?? height),
        ...style,
      }}
    />
  );
}

export interface SkeletonRegionProps {
  /** Что загружается, в винительном падеже: «обзор района». */
  label: string;
  children: ReactNode;
  className?: string;
  /** Зарезервированная высота блока. */
  minHeight?: Size;
  style?: CSSProperties;
  /**
   * Внутри уже объявленной области (скелетон страницы) блок не объявляет себя
   * второй раз: «announce loading once» (application-patterns.md).
   */
  quiet?: boolean;
}

/** Обёртка: `aria-busy`, одна живая фраза для чтения с экрана. */
export function SkeletonRegion({ label, children, className, minHeight, style, quiet }: SkeletonRegionProps) {
  return (
    <div
      className={`skel-region ${className ?? ''}`}
      aria-busy="true"
      role={quiet ? undefined : 'status'}
      aria-live={quiet ? undefined : 'polite'}
      style={{ minHeight: px(minHeight), ...style }}
      data-skeleton=""
    >
      {quiet ? null : <span className="visually-hidden">{tr(m.loading, { label })}</span>}
      {children}
    </div>
  );
}

/** Несколько строк текста; последняя короче. */
export function SkeletonText({ lines = 3, lastWidth = '62%' }: { lines?: number; lastWidth?: Size }) {
  return (
    <span className="skel-text" aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} width={i === lines - 1 && lines > 1 ? lastWidth : '100%'} />
      ))}
    </span>
  );
}

interface BlockProps {
  /** Что загружается: «темп кампании». */
  label?: string;
  quiet?: boolean;
  className?: string;
}

/** KPI-карточка: подпись, число, примечание — в размерах `KpiCard`. */
export function SkeletonKpi({ label = tr(m.kpi), quiet }: BlockProps) {
  return (
    <SkeletonRegion label={label} quiet={quiet} className="kpi skel-kpi">
      <Skeleton width="58%" className="skel--label" />
      <Skeleton variant="block" width="52%" className="skel--kpi-value" />
      <span className="kpi__spacer" />
      <Skeleton width="78%" className="skel--caption" />
    </SkeletonRegion>
  );
}

export interface SkeletonCardProps extends BlockProps {
  /** Настоящий заголовок карточки, если он известен до данных. */
  title?: ReactNode;
  /** Высота тела карточки. */
  height?: Size;
  /** Тело; по умолчанию — несколько строк текста. */
  children?: ReactNode;
  size?: 'md' | 'lg';
}

/** Карточка с заголовком и телом-скелетоном. */
export function SkeletonCard({ label = tr(m.card), title, height, children, quiet, className, size = 'md' }: SkeletonCardProps) {
  return (
    <SkeletonRegion label={label} quiet={quiet} className={`card ${size === 'lg' ? 'card--lg' : ''} skel-card ${className ?? ''}`}>
      <div className="card__head">
        {title ? <h2 className="card__title">{title}</h2> : <Skeleton width="38%" className="skel--title" />}
      </div>
      <div className="skel-card__body" style={{ minHeight: px(height) }}>
        {children ?? <SkeletonText lines={3} />}
      </div>
    </SkeletonRegion>
  );
}

/** График: оси и столбики в высоту графика. */
export function SkeletonChart({ label = tr(m.chart), title, height = 240, quiet, className, bars = 12, captionLines = 0 }: SkeletonCardProps & { bars?: number; captionLines?: number }) {
  // Детерминированный «рельеф»: одинаковый от рендера к рендеру.
  const heights = Array.from({ length: bars }, (_, i) => 30 + ((i * 37) % 55));
  return (
    <SkeletonCard label={label} title={title} quiet={quiet} className={`skel-chart ${className ?? ''}`}>
      <div className="skel-chart__plot" style={{ height: px(height) }} aria-hidden="true">
        {heights.map((h, i) => (
          <span key={i} className="skel skel--block skel-chart__bar" style={{ height: `${h}%` }} />
        ))}
      </div>
      {captionLines ? <SkeletonText lines={captionLines} lastWidth="40%" /> : null}
    </SkeletonCard>
  );
}

/** Таблица: шапка и N строк. */
export function SkeletonTable({ label = tr(m.table), rows = 6, cols = 4, quiet, className, title }: BlockProps & { rows?: number; cols?: number; title?: ReactNode }) {
  const body = (
    <div className="skel-table" aria-hidden="true" style={{ gridTemplateColumns: `2fr repeat(${Math.max(cols - 1, 0)}, 1fr)` }}>
      {Array.from({ length: cols }, (_, c) => (
        <Skeleton key={`h${c}`} width={c === 0 ? '50%' : '70%'} className="skel--label skel-table__head" />
      ))}
      {Array.from({ length: rows * cols }, (_, i) => (
        <Skeleton key={i} width={i % cols === 0 ? '82%' : `${48 + ((i * 17) % 40)}%`} className="skel-table__cell" />
      ))}
    </div>
  );
  if (title === undefined) {
    return <SkeletonRegion label={label} quiet={quiet} className={`skel-table-wrap ${className ?? ''}`}>{body}</SkeletonRegion>;
  }
  return <SkeletonCard label={label} title={title} quiet={quiet} className={className}>{body}</SkeletonCard>;
}

/** Рамка карты: сетка-подложка и мягкий проблеск, как у `MapWidget`. */
export function SkeletonMap({ label = tr(m.map), height = 420, quiet, className, note }: BlockProps & { height?: Size; note?: string }) {
  return (
    <SkeletonRegion label={label} quiet={quiet} className={`skel-map ${className ?? ''}`} style={{ height: px(height) }}>
      <span className="skel-map__shine" aria-hidden="true" />
      {note ? <span className="mapwidget__skeleton-note skel-map__note" aria-hidden="true">{note}</span> : null}
    </SkeletonRegion>
  );
}

/** Список карточек-строк: значок, две строки текста. */
export function SkeletonList({ label = tr(m.list), items = 4, quiet, className, avatar = false, tone = 'card' }: BlockProps & { items?: number; avatar?: boolean; tone?: 'card' | 'soft' }) {
  return (
    <SkeletonRegion label={label} quiet={quiet} className={`skel-list ${className ?? ''}`}>
      {Array.from({ length: items }, (_, i) => (
        <span key={i} className={`skel-list__item skel-list__item--${tone}`} aria-hidden="true">
          {avatar ? <Skeleton variant="circle" width={28} /> : null}
          <span className="skel-text">
            <Skeleton width={`${54 + ((i * 23) % 36)}%`} />
            <Skeleton width={`${30 + ((i * 13) % 30)}%`} className="skel--caption" />
          </span>
        </span>
      ))}
    </SkeletonRegion>
  );
}

/**
 * Шапка страницы в загрузке: настоящий заголовок (он известен до данных),
 * строка среза и поля управления — плашками.
 */
export function SkeletonHeader({ title, level, controls = 0, scope = true }: { title?: string; level?: string; controls?: number; scope?: boolean }) {
  return (
    <header className="pagehead" aria-hidden={title ? undefined : true}>
      <div className="pagehead__main">
        {level ? <div className="pagehead__level">{level}</div> : null}
        {title ? <h1 className="pagehead__title">{title}</h1> : <Skeleton variant="block" width={280} height={40} />}
        {scope ? <Skeleton width={420} className="skel--scope" /> : null}
      </div>
      {controls ? (
        <div className="pagehead__actions" aria-hidden="true">
          {Array.from({ length: controls }, (_, i) => (
            <Skeleton key={i} variant="block" width={i === controls - 1 ? 104 : 140} height={40} className="skel--control" />
          ))}
        </div>
      ) : null}
    </header>
  );
}

/**
 * Скелетон страницы: одна объявленная область, внутри — блоки `quiet`.
 * Без аргументов — общая форма (шапка + ряд карточек + два блока); её же
 * показывает граница `Suspense`, пока грузится код раздела.
 */
export function SkeletonPage({ label = tr(m.page), title, children, className }: { label?: string; title?: string; children?: ReactNode; className?: string }) {
  return (
    <SkeletonRegion label={label} className={`skel-page ${className ?? ''}`}>
      {children ?? (
        <>
          <SkeletonHeader title={title} />
          <div className="grid grid--4">
            {Array.from({ length: 4 }, (_, i) => <SkeletonKpi key={i} quiet />)}
          </div>
          <SkeletonCard quiet height={280} />
          <SkeletonTable quiet rows={5} />
        </>
      )}
    </SkeletonRegion>
  );
}
